import {
  ArrowRight,
  Building2,
  FileCheck,
  FileText,
  ScrollText,
  ShieldCheck,
  Vote,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { pb } from '../lib/pb';

import type { LucideIcon } from 'lucide-react';

interface Officer {
  name: string;
  role: string;
  note?: string;
}

const officers: Officer[] = [
  { name: 'Mike Sadowski', role: 'President' },
  { name: '', role: 'Vice President' },
  { name: 'Steve Stephens', role: 'Treasurer' },
  { name: 'Angelo Dvorak', role: 'Secretary' },
  { name: 'Leonard Smith', role: 'Board Member' },
];

interface ComplianceItem {
  icon: LucideIcon;
  title: string;
  body: string;
  /**
   * Substring matched against the document's original_filename. If a matching
   * doc is found at runtime, the card links directly to the file. Falls back
   * to staticHref if no match (e.g. the doc has not been uploaded yet).
   */
  docMatch?: string;
  /** Internal route used when no docMatch is set, or as a fallback. */
  staticHref?: string;
  cta: string;
}

interface ComplianceDoc {
  id: string;
  file: string;
  processed_pdf: string;
  original_filename: string;
  collectionId: string;
}

const compliance: ComplianceItem[] = [
  {
    icon: ShieldCheck,
    title: 'Reserves transitioned to full funding',
    body: 'Structural Integrity Reserve Study (SIRS, 2024) and the companion Non-Structural Reserve Study (TRS, 2024) on file with the association. Reserves transitioned to full funding.',
    docMatch: 'SHRESERVESIRSFINAL2024',
    staticHref: '/documents#governing',
    cta: 'Open the SIRS report',
  },
  {
    icon: Building2,
    title: 'Milestone Inspection on file',
    body: 'Milestone Condition Survey Report (2022) on file. Florida Statute 553.899 milestone-inspection program compliance for buildings of three stories or more.',
    docMatch: 'ConditionSurveyReport-2022',
    staticHref: '/documents#governing',
    cta: 'Open the Milestone report',
  },
  {
    icon: FileCheck,
    title: 'Wind Mitigation Reports',
    body: "Wind Mitigation Reports Parts 1 and 2 (2024) on file. Used for insurance underwriting and to document the building's wind-loss-mitigation features.",
    docMatch: 'SHWINDMITIGATION2024PART1',
    staticHref: '/documents#governing',
    cta: 'Open the Wind Mitigation reports',
  },
  {
    icon: Wallet,
    title: 'Annual financial reporting',
    body: 'Annual financial reporting per Florida Statute 718.111(13). Books reconciled monthly; the approved budget is below.',
    docMatch: 'Approved-Budget',
    staticHref: '/documents#budget',
    cta: 'Open the approved budget',
  },
  {
    icon: ScrollText,
    title: 'Records retained 7+ years',
    body: 'Meeting minutes and operating records retained at least seven years per Florida Statute 718.111(12)(b)11 and the bylaws §15.',
    docMatch: 'CONDODOCSDeclarationArticlesBylaws',
    staticHref: '/documents#governing',
    cta: 'Open the bylaws',
  },
  {
    icon: Vote,
    title: 'Quorum every year',
    body: 'Annual quorum reached every recent year. The April 2026 annual meeting reached quorum with 24 of 45 votes.',
    docMatch: 'Annual-Meeting-Minutes-unapproved',
    staticHref: '/documents#minutes',
    cta: 'See the 2026 annual meeting minutes',
  },
];

export function Board(): JSX.Element {
  const [complianceDocs, setComplianceDocs] = useState<ComplianceDoc[]>([]);
  const [fileToken, setFileToken] = useState<string>('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'The Board | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await pb.collection('documents').getList<ComplianceDoc>(1, 200, {
          filter: 'status = "ready"',
          fields: 'id,file,processed_pdf,original_filename,collectionId',
        });
        if (!cancelled) setComplianceDocs(result.items);
      } catch (err) {
        console.error('compliance doc load failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    pb.files
      .getToken()
      .then((t) => {
        if (!cancelled) setFileToken(t);
      })
      .catch((err) => {
        console.error('file token fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function resolveHref(item: ComplianceItem): { href: string; external: boolean } {
    if (item.docMatch) {
      const doc = complianceDocs.find((d) => d.original_filename.includes(item.docMatch as string));
      if (doc) {
        const opts = fileToken ? { download: false, token: fileToken } : { download: false };
        const fileUrl = pb.files.getURL(doc, doc.processed_pdf || doc.file, opts);
        return { href: fileUrl, external: true };
      }
    }
    return { href: item.staticHref ?? '/documents', external: false };
  }

  return (
    <>
      <PageHero
        eyebrow="Governance"
        title="The Board"
        lead="Sunset Harbor has been run by an owner board since 1984. Five board members, two-year terms, an annual meeting on the first Tuesday in April."
        photo={{
          src1600: '/photos/image5-1600.webp',
          src800: '/photos/image5-800.webp',
          alt: 'High aerial of Sunset Harbor in its Cocoa Beach setting: the building, carports, neighboring condominiums to the west, and the marina to the east on the Banana River.',
          objectPosition: 'center 55%',
        }}
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
              Current Officers
            </p>
            <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
              Board of Directors
            </h2>
            <p className="mt-4 text-base leading-relaxed text-brand-body-dark">
              The five board members below were elected to the 2025-2026 term at the April 2025
              annual meeting and confirmed at the organizational meeting on April 1, 2025.
            </p>
          </div>
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {officers.map((officer) => (
              <li
                key={officer.role}
                className="flex flex-col rounded-lg border border-brand-surface bg-brand-white p-7 shadow-card transition-shadow hover:shadow-card-hover"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                  {officer.role}
                </p>
                <p className="mt-3 text-xl font-bold leading-tight text-brand-charcoal">
                  {officer.name}
                </p>
                {officer.note ? (
                  <p className="mt-3 text-sm leading-relaxed text-brand-body-medium">
                    {officer.note}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="section bg-brand-surface">
        <Container>
          <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
                How the Board Operates
              </p>
              <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
                Predictable cadence. Posted notices. Real quorums.
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-brand-body-dark">
                <p>
                  Board terms are two years, set by a 1989 amendment to the bylaws. The annual
                  members&apos; meeting is held on the first Tuesday of April. The board meets at
                  least four times per fiscal year, and every meeting is noticed per Florida Statute
                  718.112. Board meetings are posted in a conspicuous place on the property at least
                  forty-eight hours in advance; the annual members&apos; meeting is mailed and
                  posted at least fourteen days in advance; budget-adoption meetings get
                  fourteen-day mailed-and-posted notice. Email reminders are sent to owners on file
                  as a courtesy.
                </p>
                <p>
                  The April 2026 annual meeting reached quorum with 24 of 45 votes (12 in person, 10
                  by proxy, 2 by phone). Quorum has been reached at every recent annual meeting.
                  Records of every motion, vote, and resolution are kept for at least seven years
                  per Florida Statute 718.111(12)(b)11.
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-6 rounded-lg bg-brand-white p-6 shadow-card md:p-8">
              {[
                { label: 'Board terms', value: '2 years' },
                { label: 'Annual meeting', value: 'First Tuesday in April' },
                { label: 'Board meetings', value: 'At least 4 per year' },
                { label: 'Voting members', value: '45 (one per unit)' },
                { label: 'Quorum (2026 annual)', value: '24 of 45 (53%)' },
                { label: 'Statute', value: 'Florida Chapter 718' },
              ].map((row) => (
                <div key={row.label}>
                  <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    {row.label}
                  </dt>
                  <dd className="mt-1 text-base font-semibold text-brand-charcoal">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Container>
      </section>

      <section className="section bg-brand-charcoal text-brand-white">
        <Container>
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow">
              Statute Compliance Posture
            </p>
            <h2 className="text-3xl font-bold tracking-heading-tight text-brand-white md:text-4xl">
              Built to clear Florida Chapter 718 cleanly
            </h2>
            <p className="mt-4 text-base leading-relaxed text-brand-white/80">
              Florida statute sets the floor for condominium governance. Sunset Harbor&apos;s
              reserves, inspections, and records-retention practices clear it with margin, and the
              evidence is on file.
            </p>
          </div>
          <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {compliance.map((item) => {
              const { icon: Icon, title, body, cta } = item;
              const { href, external } = resolveHref(item);
              const cardClasses =
                'group flex h-full flex-col rounded-lg border border-white/10 bg-white/5 p-7 backdrop-blur transition-all hover:border-brand-yellow/40 hover:bg-white/10';
              const inner = (
                <>
                  <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal transition-transform group-hover:scale-105">
                    <Icon size={22} strokeWidth={2} aria-hidden="true" />
                  </span>
                  <h3 className="text-lg font-bold text-brand-white">{title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-white/80">{body}</p>
                  <p className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-brand-yellow transition-colors group-hover:text-brand-white">
                    {cta}
                    <ArrowRight size={12} aria-hidden="true" />
                  </p>
                </>
              );
              return (
                <li key={title}>
                  {external ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cardClasses}
                    >
                      {inner}
                    </a>
                  ) : (
                    <Link to={href} className={cardClasses}>
                      {inner}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="mt-12">
            <Link
              to="/documents"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-yellow transition-colors hover:text-brand-white"
            >
              View all governance and compliance documents
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </Container>
      </section>

      <section className="section bg-brand-white">
        <Container>
          <div className="grid items-center gap-8 rounded-2xl bg-brand-charcoal p-8 text-brand-white shadow-card md:p-12 lg:grid-cols-[1.6fr_1fr] lg:gap-12">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow">
                Records Access
              </p>
              <h2 className="text-2xl font-bold tracking-heading-tight text-brand-white md:text-3xl">
                Florida Statute 718.111(12)(c) guarantees owners access to association records
                within ten working days of a written request.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-brand-white/85">
                Most categories are available on the Documents page. A small set, insurance policies
                and similar sensitive documents, are provided on request. Submit a request through
                the contact form and a board officer will respond.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <Button
                to="/contact?category=records-request"
                variant="primary"
                size="lg"
                className="w-full justify-center sm:w-auto"
              >
                Request Records
                <FileText size={18} aria-hidden="true" />
              </Button>
              <Link
                to="/documents"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-yellow transition-colors hover:text-brand-white"
              >
                Browse the document library
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
